import React from 'react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../../services/firebase';
import { fetchMedications } from '../../../services/medicationService';
import { addProvider, fetchProviders } from '../../../services/providerService';
import { saveMedicationRecord } from '../../Dashboard/shared/medicationPersistence';
import { buildMedicationReviewEditorState } from '../../Dashboard/childProfileForm/profileMedicationHydration';
import { createMedicationDetail } from '../../Dashboard/shared/childMedicationHelpers';
import { getMedicationSourceSuggestions } from '../../../features/careSourceProcessing/medicationSourceSuggestions';

const getDoseLabel = (medication = {}) => {
  const schedule = Array.isArray(medication.schedules) ? medication.schedules[0] || {} : {};
  const dose = [schedule.dose || medication.dose, schedule.unit || medication.unit]
    .filter(Boolean)
    .join(' ');
  const timing = schedule.time || medication.sourceScheduleText || medication.schedule || medication.time || '';
  return [dose, timing].filter(Boolean).join(' at ').replace('at at ', 'at ');
};

const applyMedicationReviewIntent = (medication = null, suggestion = {}) => {
  if (!medication) return medication;
  const changeType = String(suggestion.changeType || '').trim().toLowerCase();
  if (['discontinued', 'paused'].includes(changeType)) {
    return { ...medication, isArchived: true };
  }
  if (changeType === 'restarted') {
    return { ...medication, isArchived: false };
  }
  return medication;
};

const preserveUnspecifiedMedicationDetails = (draft = null, existing = null, suggestion = {}) => {
  if (!draft || !existing) return draft;
  const hasSuggestedDose = Boolean(String(suggestion.dose || '').trim());
  const hasSuggestedSchedule = Boolean(String(suggestion.schedule || '').trim());
  return {
    ...draft,
    name: existing.name,
    ...(!hasSuggestedDose ? {
      dose: existing.dose || '',
      unit: existing.unit || draft.unit,
      schedules: existing.schedules,
    } : {}),
    ...(!hasSuggestedSchedule ? {
      time: existing.time || '',
      sourceScheduleText: existing.sourceScheduleText || '',
    } : {}),
  };
};

export const useTimelineMedicationReview = ({ entryId = '', entryData = {}, entryConfig = {} } = {}) => {
  const [status, setStatus] = React.useState(entryData?.metadata?.medicationReview?.status || '');
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const [existingMedication, setExistingMedication] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [opening, setOpening] = React.useState(false);
  const [error, setError] = React.useState('');
  const [activeChildId, setActiveChildId] = React.useState('');
  const [activeSuggestion, setActiveSuggestion] = React.useState(null);
  const [medicationOptions, setMedicationOptions] = React.useState([]);
  const [providers, setProviders] = React.useState([]);
  const [providerAddOpen, setProviderAddOpen] = React.useState(false);
  const [providerDraft, setProviderDraft] = React.useState({ name: '', specialty: '', organizationName: '' });
  const [providerSaving, setProviderSaving] = React.useState(false);
  const medicationReview = entryData?.metadata?.medicationReview || null;
  const suggestions = Array.isArray(medicationReview?.suggestions) ? medicationReview.suggestions : [];
  const suggestion = suggestions[0] || null;
  const childId = entryData?.childId || entryConfig.source?.childId || '';
  const entryCollection = entryData?.collection || 'dailyLogs';
  const isPending = status === 'pending' && Boolean(suggestion);

  React.useEffect(() => {
    setStatus(entryData?.metadata?.medicationReview?.status || '');
  }, [entryData?.metadata?.medicationReview?.status]);

  const updateTimelineReviewStatus = React.useCallback(async (nextStatus, reviewChildId = childId) => {
    if (entryId) {
      await updateDoc(doc(db, entryCollection, entryId), {
        'metadata.medicationReview.status': nextStatus,
        [`metadata.medicationReview.${nextStatus}At`]: serverTimestamp(),
        [`metadata.medicationReview.${nextStatus}By`]: auth.currentUser?.uid || null,
        'metadata.needsReview': false,
        updatedAt: serverTimestamp(),
      });
    }
    setStatus(nextStatus);
    window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
      detail: { childId: reviewChildId, entryId, collection: entryCollection, source: `medication_review_${nextStatus}` },
    }));
  }, [childId, entryCollection, entryId]);

  const open = React.useCallback(async (sourceOverride = null) => {
    const sourceSuggestion = sourceOverride
      ? getMedicationSourceSuggestions(sourceOverride)[0] || null
      : null;
    const nextSuggestion = sourceSuggestion || suggestion;
    const nextChildId = sourceOverride?.childId || childId;
    if (!nextSuggestion || !nextChildId || opening) return;
    setOpening(true);
    setError('');
    try {
      const [medications, nextProviders] = await Promise.all([
        fetchMedications(nextChildId, true),
        fetchProviders(nextChildId).catch(() => []),
      ]);
      const source = sourceOverride || {
        id: entryData?.sourceNoteId || entryData?.sourceId || entryId,
        childId: nextChildId,
        rawText: entryData?.rawSourceText || entryData?.sourceText || entryData?.text || '',
        sourceContext: 'timeline',
        sourceMetadata: { fileName: 'Timeline note' },
      };
      const editorState = buildMedicationReviewEditorState({
        medicationRecords: medications,
        suggestion: nextSuggestion,
        source,
      });
      const matched = editorState.editingMedicationId
        ? editorState.medicationDetails.find((medication) => medication.id === editorState.editingMedicationId) || null
        : null;
      setExistingMedication(matched);
      const preservedDraft = preserveUnspecifiedMedicationDetails(
        editorState.medicationDraft,
        matched,
        nextSuggestion
      );
      setDraft(applyMedicationReviewIntent(preservedDraft, nextSuggestion));
      setMedicationOptions(editorState.medicationDetails);
      setProviders(nextProviders.filter((provider) => provider.isActive !== false));
      setActiveChildId(nextChildId);
      setActiveSuggestion(nextSuggestion);
      setSheetOpen(true);
    } catch (openError) {
      setError(openError?.message || 'Could not open this medication review.');
    } finally {
      setOpening(false);
    }
  }, [childId, entryData, entryId, opening, suggestion]);

  const close = React.useCallback(() => {
    if (saving || providerSaving) return;
    setSheetOpen(false);
    setDraft(null);
    setExistingMedication(null);
    setActiveChildId('');
    setActiveSuggestion(null);
    setMedicationOptions([]);
    setProviders([]);
    setProviderAddOpen(false);
    setError('');
  }, [providerSaving, saving]);

  const updateDraft = React.useCallback((field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  }, []);

  const selectMedication = React.useCallback((medicationId) => {
    const selectedMedication = medicationOptions.find((medication) => medication.id === medicationId);
    if (!selectedMedication) return;

    const nextSuggestion = {
      ...(activeSuggestion || suggestion || {}),
      medicationId: selectedMedication.id,
      name: selectedMedication.name,
    };
    const editorState = buildMedicationReviewEditorState({
      medicationRecords: medicationOptions,
      suggestion: nextSuggestion,
      source: {
        id: entryData?.sourceNoteId || entryData?.sourceId || entryId,
        childId: activeChildId || childId,
        rawText: entryData?.rawSourceText || entryData?.sourceText || entryData?.text || '',
        sourceContext: 'timeline',
        sourceMetadata: { fileName: 'Timeline note' },
      },
    });
    setExistingMedication(selectedMedication);
    const preservedDraft = preserveUnspecifiedMedicationDetails(
      editorState.medicationDraft,
      selectedMedication,
      nextSuggestion
    );
    setDraft(applyMedicationReviewIntent(preservedDraft, nextSuggestion));
    setActiveSuggestion(nextSuggestion);
  }, [activeChildId, activeSuggestion, childId, entryData, entryId, medicationOptions, suggestion]);

  const startNewMedication = React.useCallback(() => {
    const nextSuggestion = activeSuggestion || suggestion || {};
    setExistingMedication(null);
    setDraft(createMedicationDetail({
      name: nextSuggestion.name || '',
      dose: nextSuggestion.dose || '',
      unit: nextSuggestion.unit || 'mg',
      schedule: nextSuggestion.schedule || '',
      startDate: medicationReview?.effectiveDate || '',
    }));
  }, [activeSuggestion, medicationReview?.effectiveDate, suggestion]);

  const updateProviderDraft = React.useCallback((field, value) => {
    setProviderDraft((current) => ({ ...current, [field]: value }));
  }, []);

  const saveProvider = React.useCallback(async () => {
    const reviewChildId = activeChildId || childId;
    const name = providerDraft.name.trim();
    if (!reviewChildId || !name || providerSaving) return;
    setProviderSaving(true);
    setError('');
    try {
      const providerId = await addProvider({
        childId: reviewChildId,
        name,
        specialty: providerDraft.specialty,
        organizationName: providerDraft.organizationName,
        isActive: true,
        source: {
          kind: 'extracted',
          sourceId: entryData?.sourceNoteId || entryData?.sourceId || entryId || null,
          sourceLabel: 'Medication review',
        },
      });
      const nextProvider = { ...providerDraft, id: providerId, childId: reviewChildId, name, isActive: true };
      setProviders((current) => [...current, nextProvider]);
      setDraft((current) => ({ ...current, providerId }));
      setProviderAddOpen(false);
      setProviderDraft({ name: '', specialty: '', organizationName: '' });
    } catch (providerError) {
      setError(providerError?.message || 'Could not add this provider.');
    } finally {
      setProviderSaving(false);
    }
  }, [activeChildId, childId, entryData, entryId, providerDraft, providerSaving]);

  const save = React.useCallback(async () => {
    const reviewChildId = activeChildId || childId;
    if (!draft || !reviewChildId || saving || providerSaving) return null;
    setSaving(true);
    setError('');
    try {
      const saved = await saveMedicationRecord({
        childId: reviewChildId,
        medication: draft,
        previousMedication: existingMedication,
        status: 'saved',
        createTimelineEvent: true,
      });
      await updateTimelineReviewStatus('resolved', reviewChildId);
      setSheetOpen(false);
      setDraft(null);
      setActiveChildId('');
      setActiveSuggestion(null);
      return saved;
    } catch (saveError) {
      setError(saveError?.message || 'Could not save this medication change.');
      return null;
    } finally {
      setSaving(false);
    }
  }, [activeChildId, childId, draft, existingMedication, providerSaving, saving, updateTimelineReviewStatus]);

  const dismiss = React.useCallback(async () => {
    if (!isPending || !entryId) return;
    if (!window.confirm('Keep this note on the Timeline, but remove the medication review?')) return;
    await updateTimelineReviewStatus('dismissed');
  }, [entryId, isPending, updateTimelineReviewStatus]);

  return {
    status,
    isPending,
    sheetOpen,
    draft,
    existingMedication,
    medicationOptions,
    providers,
    providerAddOpen,
    providerDraft,
    providerSaving,
    selectionRequired: sheetOpen && !draft,
    currentLabel: getDoseLabel(existingMedication || {}),
    proposedLabel: getDoseLabel(draft || activeSuggestion || suggestion || {}),
    opening,
    saving,
    error,
    open,
    close,
    updateDraft,
    selectMedication,
    startNewMedication,
    openProviderAdd: () => setProviderAddOpen(true),
    closeProviderAdd: () => setProviderAddOpen(false),
    updateProviderDraft,
    saveProvider,
    save,
    dismiss,
  };
};

export default useTimelineMedicationReview;
